/**
 * ClientColumn — trigger lane: input + messages клиента + chip подсказки.
 */

import { useRef, useEffect } from 'react';
import type { DemoRound } from './DemoExperience';

// ============================================================================
// Chips
// ============================================================================

const CHIPS = [
  { text: 'Сколько стоит ваш продукт?', highlighted: true },
  { text: 'Как подключить Telegram?', highlighted: false },
  { text: 'Есть ли бесплатный период?', highlighted: false },
  { text: 'Чем вы отличаетесь от обычного чат-бота?', highlighted: false },
];

// ============================================================================
// Props
// ============================================================================

interface ClientColumnProps {
  rounds: DemoRound[];
  inputValue: string;
  onInputChange: (v: string) => void;
  onSend: () => void;
  onChipClick: (text: string) => void;
  disabled: boolean;
  isPending: boolean;
  limitReached: boolean;
}

// ============================================================================
// Component
// ============================================================================

export function ClientColumn({
  rounds,
  inputValue,
  onInputChange,
  onSend,
  onChipClick,
  disabled,
  isPending,
  limitReached,
}: ClientColumnProps): JSX.Element {
  const scrollRef = useRef<HTMLDivElement>(null);
  const prevRoundsLengthRef = useRef(0);

  useEffect(() => {
    if (rounds.length <= prevRoundsLengthRef.current) return;
    prevRoundsLengthRef.current = rounds.length;
    const container = scrollRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
  }, [rounds.length]);

  return (
    <>
      {/* Header */}
      <div className="px-4 py-3 border-b border-border-subtle">
        <span className="text-[10px] font-bold text-text-muted uppercase tracking-wider">Клиент</span>
      </div>

      {/* Messages + Chips */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
        {rounds.length === 0 && (
          <div className="space-y-3">
            <p className="text-xs text-text-muted">Напишите вопрос от лица клиента</p>
            <div className="flex flex-wrap gap-1.5">
              {CHIPS.map(chip => (
                <button
                  key={chip.text}
                  type="button"
                  onClick={() => onChipClick(chip.text)}
                  disabled={disabled}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium rounded-full transition-colors disabled:opacity-50 ${
                    chip.highlighted
                      ? 'bg-accent-brand-light text-accent-brand border border-accent-brand-border hover:bg-accent-brand/10'
                      : 'bg-surface-subtle text-text-secondary border border-border-subtle hover:bg-surface-base'
                  }`}
                >
                  {chip.highlighted && (
                    <>
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                      </svg>
                      <span className="text-[9px] opacity-70">Начните с этого:</span>
                    </>
                  )}
                  {chip.text}
                </button>
              ))}
            </div>
          </div>
        )}

        {rounds.map(round => (
          <div key={round.clientMessage.id} className="flex justify-end">
            <div className="max-w-[85%] bg-accent-brand text-white rounded-2xl rounded-br-sm px-3 py-2 text-sm">
              <p className="whitespace-pre-wrap">{round.clientMessage.text}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Input */}
      {!limitReached && (
        <div className="px-3 py-3 border-t border-border-subtle flex gap-2">
          <textarea
            value={inputValue}
            onChange={e => onInputChange(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                onSend();
              }
            }}
            placeholder={disabled ? 'Подождите...' : 'Напишите как клиент...'}
            disabled={disabled}
            maxLength={500}
            aria-label="Сообщение клиента"
            className="flex-1 resize-none rounded-comp border border-border-subtle px-3 py-2 text-sm outline-none focus:border-accent-brand bg-surface-base text-text-primary placeholder:text-text-muted disabled:opacity-50"
            rows={1}
          />
          <button
            type="button"
            onClick={onSend}
            disabled={disabled || !inputValue.trim() || isPending}
            className="px-3 py-2 rounded-comp bg-accent-brand text-white text-sm font-medium hover:bg-accent-brand/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Отправить"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
            </svg>
          </button>
        </div>
      )}
    </>
  );
}
